// composables/useDecisionsInbox.js
//
// The "قرارات مطلوبة" (decisions-required) hub data layer, plus the member's
// past-decisions history.
//
// WAS DERIVED, NOW DIRECT: Phase 2 shipped without an issues index route, so this
// scanned every ACTIVE/READY box's items and kept the ones whose compact
// `activeIssue.awaitingMember` signal was true — 1+N calls, and blind to any box
// the scan didn't cover. Interaction-v2 added `GET /api/v1/item-issues`, scoped
// server-side to the acting member's own boxes (ItemIssueSchema::indexQuery — a
// member still cannot enumerate anyone else's). That makes this ONE call, makes
// the awaiting count AUTHORITATIVE rather than a best-effort scan, and hands us
// the answered/resolved issues the derived scan could never see (`activeIssue` is
// null once an issue resolves) — which is what the history section renders.
//
// NOT PAGINATED, deliberately: ItemIssueSchema declares no `pagination()`, so any
// `page[...]` param is a 400 and the route returns the member's whole history.
// Fine at a member's scale today; see the FE→BE brief
// `2026-07-25-item-issue-index-pagination-and-member-payload-echo.md` for the ask.
//
// Plain deserialized rows in a ref (mirrors usePoolaClaims) — nothing here mutates
// an item, and staying out of the pinia-orm store avoids any cross-session
// `.get()` staleness leaking a resolved issue back into the list.
import { computed, ref } from 'vue';
import {
  ISSUE_STATUS,
  issuePillVariant,
  terminalOutcomeIsNeutral,
  terminalOutcomeKey,
} from '@/constants/collectionIssue.js';
import {
  fetchMemberIssues,
  ITEM_ISSUE_ALREADY_HANDLED,
} from '@/services/itemIssue.service.js';
import { itemDisplayName } from '@/utils/orderBoxItemDisplay.js';

/** One issue → one list row. */
function toRow(issue) {
  const item = issue.orderBoxItem ?? null;
  return {
    issueId: String(issue.id),
    itemId: item?.id != null ? String(item.id) : null,
    orderBoxId: item?.orderBoxId != null ? String(item.orderBoxId) : null,
    // A REPORTING TAG, and the row treats it as one — subordinate to `question`
    // below. A member who reads only «نواقص في الكمية» learns nothing she can
    // act on.
    reason: issue.reason ?? null,
    // The leader's own words — what the row LEADS with, in both lists.
    // `ItemIssueResource::attributes()` serves this on the index exactly as it
    // does on `show`, so there is no second fetch and no per-row gap.
    question: issue.question ?? null,
    status: issue.status ?? null,
    name: itemDisplayName(item),
    avatarUrl: item?.avatarUrl || item?.productAvatarUrl || null,
    isManual: Boolean(item?.isManual),
    // Whose court, for the row's pill — same helper the detail screen uses.
    pillVariant: issuePillVariant(issue),
    createdAt: issue.createdAt ?? null,
    // What actually happened, read off the SAME `timeline` the index route
    // already returns per row (ItemIssueSchema eager-loads `transitions` via
    // `$with`, so this is free — no per-row fetch). null on any non-terminal
    // issue, or defensively if a future payload ever omits `timeline`; the row
    // falls back to the pill in that case.
    outcomeKey: terminalOutcomeKey(issue.timeline),
    // Gates the row's success styling — a withdrawal is terminal but not a win.
    outcomeIsNeutral: terminalOutcomeIsNeutral(issue.timeline),
  };
}

export function useDecisionsInbox() {
  const rows = ref([]); // AWAITING the member — the actionable list
  const historyRows = ref([]); // answered (leader's court) + resolved
  const loading = ref(false);
  const error = ref(null);
  const loaded = ref(false); // an initial load has resolved at least once

  const count = computed(() => rows.value.length);
  // Empty ONLY after a load resolved — before that the page shows the skeleton,
  // so opening the inbox never flashes the empty state. Scoped to the ACTIONABLE
  // list: a member with nothing waiting but a full history still gets the warm
  // "all caught up" close, with the history below it.
  const isEmpty = computed(() => loaded.value && rows.value.length === 0);
  const hasHistory = computed(() => historyRows.value.length > 0);

  async function load() {
    // One request now, so a plain in-flight guard replaces the AbortController
    // the 1+N box scan needed: a second caller (PTR during the mount load) just
    // rides on the first instead of racing it.
    if (loading.value) return;

    loading.value = true;
    error.value = null;

    try {
      const { issues } = await fetchMemberIssues();

      const awaiting = [];
      const history = [];
      for (const issue of issues) {
        if (!issue || issue.id == null) continue;
        const row = toRow(issue);
        if (issue.status === ISSUE_STATUS.AWAITING_MEMBER) awaiting.push(row);
        else history.push(row);
      }

      // The server returns `sort=-createdAt` (newest first), right for history.
      // The awaiting list wants the OPPOSITE: oldest-waiting-first, since those
      // are closest to the plan closing — reverse just this partition rather
      // than re-sorting (the fetch order is already newest-first, so a plain
      // reverse gives oldest-first with no comparator needed).
      rows.value = awaiting.reverse();
      historyRows.value = history;
      loaded.value = true;
    } catch (err) {
      if (err?.code === ITEM_ISSUE_ALREADY_HANDLED) {
        // 403/404 — the feature is dark or this account can't read the surface.
        // An empty, calm hub is the honest render, not an error state.
        rows.value = [];
        historyRows.value = [];
        loaded.value = true;
        return;
      }
      error.value = err;
      // eslint-disable-next-line no-console
      console.error('[useDecisionsInbox] load error:', err);
    } finally {
      loading.value = false;
    }
  }

  async function refresh(done) {
    try {
      await load();
    } finally {
      if (typeof done === 'function') done();
    }
  }

  /** Load once (first open); a no-op if already loaded or in flight. */
  function ensureLoaded() {
    if (loaded.value || loading.value) return Promise.resolve();
    return load();
  }

  return {
    rows,
    historyRows,
    count,
    loading,
    error,
    loaded,
    isEmpty,
    hasHistory,
    load,
    refresh,
    ensureLoaded,
  };
}
